import React, { MouseEventHandler } from "react";
import styled, { css } from "styled-components";
import { Form } from "formik";
import { Button, Theme } from "@literati/public-ui-shared-react";
import ShippingSuggestedAddress from "components/Shipping/ShippingSuggestedAddress";
import ShippingFields, {
  ShippingFieldsProps,
} from "components/Shipping/ShippingFields";
import { Address } from "components/Shipping/shippingTypes";
import StepFormButton from "components/Step/StepFormButton";

const fullWidth = css`
  max-width: none;
  width: 100%;
`;

type ButtonType = "confirm" | "edit";

const StyledButton = styled(Button)<{ $buttonType: ButtonType }>`
  ${fullWidth}
  margin: ${({ $buttonType }) =>
    $buttonType === "confirm" ? "1rem 0 0" : "2rem 0 0"};
`;

const StyledFormButton = styled(StepFormButton)`
  ${fullWidth}
  margin: 1rem 0 0;
`;

export interface ShippingFormContentProps
  extends Omit<ShippingFieldsProps, "className"> {
  formId?: string;
  onConfirm?: React.MouseEventHandler<HTMLButtonElement>;
  onEdit?: MouseEventHandler<HTMLButtonElement>;
  submittedAddress?: Address;
  suggestedAddress?: Address;
}

export function ShippingFormContentInline({
  formId,
  onConfirm,
  onEdit,
  submittedAddress,
  suggestedAddress,
  ...shippingFieldProps
}: ShippingFormContentProps) {
  const ctaText = "Continue with this address";

  return (
    <>
      {submittedAddress && suggestedAddress ? (
        <>
          <ShippingSuggestedAddress
            submittedAddress={submittedAddress}
            suggestedAddress={suggestedAddress}
          />
          <StyledButton
            $buttonType="edit"
            onClick={onEdit}
            type="button"
            variant="secondary"
          >
            Edit Address
          </StyledButton>
          {formId ? (
            <StyledFormButton form={formId}>{ctaText}</StyledFormButton>
          ) : (
            <StyledButton
              $buttonType="confirm"
              onClick={onConfirm}
              type="button"
              variant="primary"
            >
              {ctaText}
            </StyledButton>
          )}
        </>
      ) : (
        <ShippingFields {...shippingFieldProps} />
      )}
    </>
  );
}

export interface ShippingFormProps extends ShippingFormContentProps {
  className?: string;
  suggestedAddressVariant?: "inline" | "modal";
}

export function ShippingForm({
  className = "",
  suggestedAddressVariant = "inline",
  ...contentProps
}: ShippingFormProps) {
  return (
    <Theme>
      <Form className={className} id={contentProps.formId}>
        {suggestedAddressVariant === "inline" ? (
          <ShippingFormContentInline {...contentProps} />
        ) : null}
      </Form>
    </Theme>
  );
}

export default ShippingForm;
